import React, { useEffect, useMemo, useState } from "react"
import {
  Badge,
  Card,
  CardBody,
  CardTitle,
  Col,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
  Table,
} from "reactstrap"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import Breadcrumbs from "components/Common/Breadcrumb"
import BackButton from "components/Shared/BackButton"
import { ShimmerThumbnail } from "react-shimmer-effects"
import { getCityDetails } from "store/actions/cityAction"
import { getAllZoneList } from "store/actions/zoneAction"
import isPermissionEnabled, {
  getSubPermissions,
} from "common/isPermissionEnabled"
import { useTranslation } from "react-i18next"
import CityZonesMap from "./CityZonesMap"

const CityDetails = () => {
  const { t } = useTranslation()

  document.title = t(`City Details `)

  const location = useLocation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get("id")
  const tabParam = searchParams.get("tab")

  const { loading, cityDetails } = useSelector(state => state.City)
  const { zoneList } = useSelector(state => state.Zone)

  const { userRole, accountType } = useSelector(state => state.Login)

  const cityViewSubPermissions = useMemo(
    () => getSubPermissions(userRole, 26, "city_1", accountType) || {},
    [userRole, accountType]
  )

  const hasMapPermission = Boolean(
    cityViewSubPermissions?.city_map?.isEnabled &&
      isPermissionEnabled(userRole, 26, "city_1", accountType)
  )

  const showMapTab = hasMapPermission

  const [activeTab, setActiveTab] = useState("details")
  const [zonesLoading, setZonesLoading] = useState(false)

  useEffect(() => {
    const isPermissionView = isPermissionEnabled(
      userRole,
      26,
      "city_1",
      accountType
    )
    if (!isPermissionView) {
      navigate("/dashboard")
    }
  }, [])

  useEffect(() => {
    const fetchOldDetails = () => {
      const payload = {
        id,
      }
      dispatch(getCityDetails(payload))
    }
    fetchOldDetails()
  }, [])

  useEffect(() => {
    if (tabParam === "map" && showMapTab) {
      setActiveTab("map")
    } else if (!showMapTab) {
      setActiveTab("details")
    }
  }, [showMapTab, tabParam])

  useEffect(() => {
    if (!showMapTab || !id) return

    setZonesLoading(true)
    dispatch(getAllZoneList())
      .finally(() => setZonesLoading(false))
  }, [showMapTab, id, dispatch])

  const cityZones = useMemo(
    () =>
      (zoneList || []).filter(
        zone => String(zone?.city?._id || zone?.city) === String(id)
      ),
    [zoneList, id]
  )

  const handleTabChange = tab => {
    setActiveTab(tab)
    const nextParams = new URLSearchParams(location.search)
    if (tab === "map") {
      nextParams.set("tab", "map")
    } else {
      nextParams.delete("tab")
    }
    navigate(`${location.pathname}?${nextParams.toString()}`, { replace: true })
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <CardTitle className="d-flex align-items-center">
                  <BackButton /> {t("City Details")}
                </CardTitle>

                {showMapTab && (
                  <Nav tabs className="mb-3">
                    <NavItem>
                      <NavLink
                        className={activeTab === "details" ? "active" : ""}
                        style={{ cursor: "pointer" }}
                        onClick={() => handleTabChange("details")}
                      >
                        {t("Details")}
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={activeTab === "map" ? "active" : ""}
                        style={{ cursor: "pointer" }}
                        onClick={() => handleTabChange("map")}
                      >
                        {t("Map")}
                      </NavLink>
                    </NavItem>
                  </Nav>
                )}

                <TabContent activeTab={activeTab}>
                  <TabPane tabId="details">
                    <div className="table-responsive">
                      {loading ? (
                        <ShimmerThumbnail height={650} rounded />
                      ) : Object.values(cityDetails).length === 0 ? (
                        <p className="text-center my-2">
                          {t("No city details found")}
                        </p>
                      ) : (
                        <Table className="table-nowrap mb-0 capitalize_td">
                          <tbody>
                            <tr>
                              <th scope="row">{t("Id")}</th>
                              <td>{cityDetails._id}</td>
                            </tr>
                            <tr>
                              <th scope="row">{t("Name(English)")}</th>
                              <td>{cityDetails?.name}</td>
                            </tr>
                            <tr>
                              <th scope="row">{t("Name(عربي)")}</th>
                              <td>{cityDetails?.nameAr}</td>
                            </tr>

                            <tr>
                              <th scope="row">{t("Region Name(English)")}</th>
                              <td>{cityDetails?.region?.name}</td>
                            </tr>
                            <tr>
                              <th scope="row">{t("Region Name(عربي)")}</th>
                              <td>{cityDetails?.region?.nameAr}</td>
                            </tr>

                            <tr>
                              <th scope="row">{t("Country Name(English)")}</th>
                              <td>{cityDetails?.country?.name}</td>
                            </tr>
                            <tr>
                              <th scope="row">{t("Country Name(عربي)")}</th>
                              <td>{cityDetails?.country?.nameAr}</td>
                            </tr>

                            <tr>
                              <th scope="row">{t("Status")}</th>
                              <td>
                                {cityDetails?.status === 1 ? (
                                  <Badge className="bg-success">
                                    {t("Active")}
                                  </Badge>
                                ) : (
                                  <Badge className="bg-danger">
                                    {t("Deactive")}
                                  </Badge>
                                )}
                              </td>
                            </tr>
                          </tbody>
                        </Table>
                      )}
                    </div>
                  </TabPane>

                  {showMapTab && (
                    <TabPane tabId="map">
                      {zonesLoading ? (
                        <ShimmerThumbnail height={500} rounded />
                      ) : (
                        <CityZonesMap zones={cityZones} />
                      )}
                    </TabPane>
                  )}
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default CityDetails
